import { Button, Text } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { SubscriptionStatus } from '__generated__/atlas-types'

import { formatDate, subtractDateUnit } from 'utils/dates'
import { decimalWithCommas } from 'utils/formatters'

import useRerenderOnSetLanguage from 'hooks/useRerenderOnSetLanguage'
import { useToasts } from 'hooks/useToasts'
import { getDateFormat } from 'store/currentUser/selectors'

import { PremiumFeatureFromHook } from '.'
import { ManageBillingAndSubsLink } from './ManageBillingAndSubsLink'
import { useCreateStripeProductLink } from 'features/Subscriptions/mutations.hooks'
import { FeatureCardIds } from 'features/Subscriptions/subscriptions.ids'

type PriceInfoFromQuery = NonNullable<PremiumFeatureFromHook['prices']>[0]

function pricesSortedByAmountAsc(
  prices: PremiumFeatureFromHook['prices'],
): PriceInfoFromQuery[] {
  if (!prices?.length) return []
  return [...prices].sort((a, b) => Number(a.amount) - Number(b.amount))
}

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

const RightSideWrapper = styled.div`
  border-left: 1px solid var(--color-primary-200);
  padding: 10px 20px 16px;
  min-height: 180px;

  &.borderless {
    border: none;
    padding-left: 0;
    padding-bottom: 0;
  }

  @media (max-width: 1180px) {
    height: auto;
    padding: 0 0 0 12px;
  }
`

const SubscriptionTypesWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;

  .price {
    display: grid;
    grid-template-rows: 34px 60px 34px;
    justify-items: center;
    gap: 18px;
    padding-top: 15px;
    padding-bottom: 15px;
  }

  > * {
    display: flex;
    flex-direction: column;
    align-items: center;
    }
  }

  > *:not(:first-child) {
    border-left: 1px solid var(--color-primary-200);
    padding-left: 20px;
    margin-left: 20px;
  }

  @media (min-width: 1300px) {
    > *:not(:first-child) {
      padding-left: 30px;
      margin-left: 30px;
    }
  }
`

const SubInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-right: 20px;

  .disabled-text {
    cursor: not-allowed;
  }

  @media (max-width: 1180px) {
    padding-right: 0;
    gap: 16px;
  }
`

const SubscriptionDetails = ({
  priceInfo,
  setIsOpen,
  isInOpenTrial,
}: {
  priceInfo: PriceInfoFromQuery
  setIsOpen: (isOpen: boolean) => void
  isInOpenTrial?: boolean
}) => {
  const { createStripeProductLink, loading } = useCreateStripeProductLink()
  const { showErrorToast } = useToasts()

  const hasTrial = Boolean(
    priceInfo.trialDays && priceInfo.trialDays > 0 && !isInOpenTrial,
  )

  const handleClick = async () => {
    setIsOpen(true)
    try {
      const result = await createStripeProductLink(priceInfo.id)
      if (result.data?.createStripeProductLink) {
        window.location.assign(result.data?.createStripeProductLink)
      } else {
        throw new Error()
      }
    } catch (err) {
      setIsOpen(false)
      showErrorToast(
        'An error occurred while redirecting to the checkout page. Please try again.',
      )
    }
  }

  return (
    <div className="price">
      <Text as="span" color="primary-500">
        <Text
          as="span"
          fontWeight="bold"
          fontSize="large"
          fontFamily="numeric"
          margin="0 12px 0 0"
          color="primary-900"
        >
          ${decimalWithCommas(priceInfo.amount)}
        </Text>
        USD
      </Text>
      <Text className="billing-info" textAlign="center">
        <span>Billed</span> <span>{priceInfo.frequency.toLowerCase()}</span>{' '}
        <span>{hasTrial && `after ${priceInfo.trialDays}-day trial`}</span>
      </Text>
      <Button
        className="cta-button"
        loading={loading}
        onClick={handleClick}
        id={FeatureCardIds.PurchaseButton + `-${priceInfo.id}`}
      >
        {hasTrial ? 'Start Free Trial' : 'Purchase'}
      </Button>
    </div>
  )
}

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const RightSide = ({
  feature,
  setIsOpen,
}: {
  feature: PremiumFeatureFromHook
  setIsOpen: (isOpen: boolean) => void
}) => {
  useRerenderOnSetLanguage()
  const { showErrorToast } = useToasts()
  const { createStripeProductLink } = useCreateStripeProductLink()

  const userDateFormat = useSelector(getDateFormat) as string
  const { subscriptionsView } = feature
  const currentSubscription = subscriptionsView?.current
  const isLoadingAfterPay =
    currentSubscription?.status === SubscriptionStatus.PROCESSING
  const isSubscriptionActive =
    currentSubscription?.status === SubscriptionStatus.ACTIVE
  const isSubscriptionCancelled =
    currentSubscription?.status === SubscriptionStatus.CANCELLED
  const isSubscriptionInPaymentFailure =
    currentSubscription?.status === SubscriptionStatus.EXPIRED
  const isSubscriptionScheduled =
    currentSubscription?.status === SubscriptionStatus.SCHEDULED
  const isFeatureAvailable =
    currentSubscription?.status === SubscriptionStatus.AVAILABLE_FOR_PURCHASE
  const isTrial = !!currentSubscription?.isTrial
  // changing price (e.g. 10 monthly to 100 yearly) within a feature
  const isPriceChange =
    currentSubscription &&
    (feature?.subscriptionsView?.count || 0) > 1 &&
    feature?.subscriptionsView?.scheduledSubscriptions?.length
  const isOwner = !!feature?.subscriptionsView?.current?.isOwner
  const packagesStatus = feature.packagesStatus
  const allPackagesAreAvailable = packagesStatus?.every(
    (pkg) => pkg.status === SubscriptionStatus.AVAILABLE_FOR_PURCHASE,
  )

  const handleCustomerPortalClick = async () => {
    try {
      setIsOpen(true)
      const priceId = currentSubscription?.priceId
      const res = await createStripeProductLink(priceId)
      if (res?.data?.createStripeProductLink) {
        window.location.assign(res.data.createStripeProductLink)
      } else {
        throw new Error()
      }
    } catch (err) {
      setIsOpen(false)
      showErrorToast(
        'An error occurred while redirecting to the customer portal. Please try again.',
      )
    }
  }

  if (isFeatureAvailable) {
    const isInOpenTrial =
      typeof feature?.openTrialDaysRemaining === 'number' &&
      !Number.isNaN(feature?.openTrialDaysRemaining) &&
      feature?.openTrialDaysRemaining > 0
    const openTrialDaysRemaining = feature?.openTrialDaysRemaining || 0

    // the multiplication converts the openTrialDaysRemaining - 1 into miliseconds
    // and from that timestamp we create the new date
    const openTrialEndDate = new Date(
      Date.now() + (openTrialDaysRemaining - 1) * 86400000,
    )

    const pricesOrderedByAmount = pricesSortedByAmountAsc(feature.prices)

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100%',
        }}
      >
        <SubscriptionTypesWrapper>
          {pricesOrderedByAmount.map((price) => (
            <SubscriptionDetails
              key={price.id}
              priceInfo={price}
              setIsOpen={setIsOpen}
              isInOpenTrial={isInOpenTrial}
            />
          ))}
        </SubscriptionTypesWrapper>
        {isInOpenTrial && allPackagesAreAvailable && (
          <Text
            className={FeatureCardIds.OpenFreeTrialText}
            margin="15px 0 0 0"
            fontSize="small"
          >
            {window?.Localize?.translate?.(
              'Free Trial ends %{date}. Purchase now to stay subscribed. No charges until then.',
              {
                date: `${formatDate(openTrialEndDate, userDateFormat)} `,
              },
            )}
          </Text>
        )}
      </div>
    )
  }

  if (isLoadingAfterPay) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Text letterSpacing="tight" color="warning-800" textAlign="center">
          {'It may take a few minutes for you to gain access to this feature.' +
            ' Feel free to navigate away or close this page.' +
            ' This process will continue in the background.'}
        </Text>
      </div>
    )
  }

  if (isPriceChange) {
    const scheduledSubscription =
      feature?.subscriptionsView?.scheduledSubscriptions[0]!

    const expiryDate = formatDate(
      subtractDateUnit(currentSubscription.expiryDate, 1),
      userDateFormat,
    )
    const startDate = formatDate(
      scheduledSubscription.startDate,
      userDateFormat,
    )

    const activeSubInfoLabel = `${capitalize(currentSubscription.frequency)} Subscription - Active`
    const activeSubDateLabel = `Ends on ${expiryDate}`
    const scheduledSubInfoLabel = `${capitalize(scheduledSubscription.frequency)} Subscription - Scheduled`
    const scheduledSubDateLabel = `Begins on ${startDate}`

    return (
      <SubInfoWrapper>
        <TextWrapper>
          <Text className="sub-frequency">
            <span>{activeSubInfoLabel}</span>
            <br />
            <span>{activeSubDateLabel}</span>
          </Text>
          <Text className="sub-frequency">
            <span>{scheduledSubInfoLabel}</span>
            <br />
            <span>{scheduledSubDateLabel}</span>
          </Text>
          <Text lineHeight="20px">
            <ManageBillingAndSubsLink
              disabled={!isOwner}
              onClick={() => handleCustomerPortalClick()}
            />
          </Text>
        </TextWrapper>
      </SubInfoWrapper>
    )
  }

  if (isSubscriptionCancelled) {
    const formattedDate = formatDate(
      currentSubscription.expiryDate,
      userDateFormat,
    )

    return (
      <SubInfoWrapper>
        <TextWrapper>
          <Text className="sub-frequency">
            <span>{`${capitalize(currentSubscription.frequency)} Subscription`}</span>{' '}
            -{' '}
            <Text as="span" color="error-600">
              Cancelled
            </Text>
          </Text>
          <Text>{`Ends on ${formattedDate}`}</Text>
        </TextWrapper>
        <Text lineHeight="20px">
          <ManageBillingAndSubsLink
            disabled={!isOwner}
            onClick={() => handleCustomerPortalClick()}
          />
          {window.Localize?.translate?.(
            'before subscription ends to keep %{featureName}',
            { featureName: feature.name },
          )}
          .
        </Text>
      </SubInfoWrapper>
    )
  }

  if (isSubscriptionActive || isSubscriptionScheduled) {
    const isScheduled = isTrial || isSubscriptionScheduled

    const expiryDate = formatDate(
      subtractDateUnit(currentSubscription.expiryDate, 1),
      userDateFormat,
    )
    const startOrRenewalDate = formatDate(
      currentSubscription.expiryDate,
      userDateFormat,
    )

    const freeTrialLabel = 'Free Trial - Active'
    const freeTrialEndDateLabel = `Ends on ${expiryDate}`

    const subInfoLabel = `${capitalize(
      currentSubscription.frequency,
    )} Subscription - ${isScheduled ? 'Scheduled' : 'Active'}`
    const subDateLabel = isScheduled
      ? 'Begins on %{date}'
      : 'Auto-renews on %{date}'

    return (
      <SubInfoWrapper>
        <TextWrapper>
          {isTrial && (
            <Text className="sub-frequency">
              <span>{freeTrialLabel}</span>
              <br />
              <span>{freeTrialEndDateLabel}</span>
            </Text>
          )}
          <Text className="sub-frequency">
            <span>{subInfoLabel}</span>
            <br />
            <span>
              {window?.Localize?.translate?.(subDateLabel, {
                date: startOrRenewalDate,
              })}
            </span>
          </Text>
          <Text lineHeight="20px">
            <ManageBillingAndSubsLink
              disabled={!isOwner}
              onClick={() => handleCustomerPortalClick()}
            />
          </Text>
        </TextWrapper>
      </SubInfoWrapper>
    )
  }

  if (isSubscriptionInPaymentFailure) {
    const dateString = formatDate(
      currentSubscription.expiryDate,
      userDateFormat,
    )

    return (
      <SubInfoWrapper>
        <Text className={!isOwner ? 'disabled-text' : ''} lineHeight="20px">
          <ManageBillingAndSubsLink
            disabled={!isOwner}
            onClick={() => handleCustomerPortalClick()}
            text="Update billing"
          />{' '}
          {window.Localize?.translate?.(
            'by %{date} to prevent feature interruption.',
            {
              date: dateString,
            },
          )}
        </Text>
      </SubInfoWrapper>
    )
  }

  return null
}

export const StatusOrAction = ({
  feature,
  setIsOpen,
}: {
  feature: PremiumFeatureFromHook
  setIsOpen: (isOpen: boolean) => void
}) => {
  return (
    <RightSideWrapper
      className={
        feature?.subscriptionsView?.current?.status ===
        SubscriptionStatus.AVAILABLE_FOR_PURCHASE
          ? 'borderless'
          : ''
      }
    >
      <RightSide feature={feature} setIsOpen={setIsOpen} />
    </RightSideWrapper>
  )
}
